import type {
  FaceUiColorScheme,
  FaceUiContentLayout,
  FaceUiDefinition,
  FaceUiFaceFilter,
  FaceUiIconAsset,
  FaceUiPoint,
} from '../ui/FaceUi';
import rawConfig from '../config/ui/face-ui/craft.json';
import rawTemplates from '../config/ui/face-ui/templates.json';

interface ConfigIcon {
  alias?: string;
  src?: string;
  rotation?: number;
}

interface CraftFaceUiConfig {
  id: string;
  faces?: FaceUiFaceFilter;
  defaultLayout: string;
  colors?: FaceUiColorScheme;
  icons?: Record<string, Omit<ConfigIcon, 'alias'>>;
  layouts: Array<{
    id: string;
    template: string;
    colors?: FaceUiColorScheme;
    content?: FaceUiContentLayout;
    buttons: Array<{
      id: string;
      text?: string;
      tooltip?: string;
      icon?: ConfigIcon | false;
      content?: FaceUiContentLayout;
    }>;
  }>;
}

const config = rawConfig as unknown as CraftFaceUiConfig;
const templates = rawTemplates as unknown as Record<string, FaceUiPoint[][]>;

export const CRAFT_FACE_UI_ID = config.id;

export const CRAFT_FACE_UI: FaceUiDefinition = {
  id: config.id,
  faces: config.faces,
  defaultLayout: config.defaultLayout,
  colors: config.colors,
  layouts: config.layouts.map((layout) => {
    const slots = templates[layout.template];
    if (!slots || slots.length !== layout.buttons.length) {
      throw new Error(
        `Face UI template "${layout.template}" needs ${layout.buttons.length} slots`,
      );
    }
    return {
      id: layout.id,
      colors: layout.colors,
      content: layout.content,
      buttons: layout.buttons.map((button, index) => ({
        id: button.id,
        text: button.text,
        tooltip: button.tooltip,
        content: button.content,
        polygon: slots[index],
        icon: configuredIcon(config.icons, button.icon),
      })),
    };
  }),
};

function configuredIcon(
  aliases: Record<string, Omit<ConfigIcon, 'alias'>> | undefined,
  reference: ConfigIcon | false | undefined,
): FaceUiIconAsset | undefined {
  if (!reference) return undefined;
  const aliased = reference.alias ? aliases?.[reference.alias] : undefined;
  if (reference.alias && !aliased) {
    throw new Error(`Unknown Face UI icon alias: ${reference.alias}`);
  }
  const icon = { ...aliased, ...reference };
  delete icon.alias;
  return icon.src ? icon as FaceUiIconAsset : undefined;
}
